/**
 * Client HTTP minimaliste pour l'API backend.
 *
 * - Inclut systématiquement les cookies (credentials: "include") pour la
 *   session HttpOnly côté serveur.
 * - Convertit les erreurs API au format uniforme `{error: {code, message}}`
 *   en exception typée `ApiError`.
 * - Pas de retry automatique en V1 (TanStack Query peut le faire au-dessus).
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export type ApiErrorPayload = {
  error: { code: string; message: string; details?: unknown };
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  // `keepalive` permet à la requête de survivre à la fermeture de l'onglet
  // (flush autosave sur beforeunload). Limité à ~64 ko de body par le navigateur.
  keepalive?: boolean;
};

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    signal: opts.signal,
    keepalive: opts.keepalive,
  };

  if (opts.body !== undefined) {
    init.headers = { ...init.headers, "Content-Type": "application/json" };
    init.body = JSON.stringify(opts.body);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    throw new ApiError(0, "NETWORK_ERROR", "Network request failed", err);
  }

  // Pas de contenu (204) : on renvoie undefined cast
  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const code = extractErrorCode(data) ?? `HTTP_${response.status}`;
    const message = extractErrorMessage(data) ?? response.statusText;
    const details = extractErrorDetails(data);
    throw new ApiError(response.status, code, message, details);
  }

  return data as T;
}

function extractErrorCode(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  // FastAPI place notre détail métier dans data.detail.error.code
  const detail = (data as Record<string, unknown>).detail;
  if (typeof detail === "object" && detail !== null && "error" in detail) {
    const err = (detail as Record<string, unknown>).error;
    if (typeof err === "object" && err !== null && "code" in err) {
      return String((err as Record<string, unknown>).code);
    }
  }
  // Cas où l'erreur est directement {error: {...}}
  if ("error" in (data as Record<string, unknown>)) {
    const err = (data as Record<string, unknown>).error;
    if (typeof err === "object" && err !== null && "code" in err) {
      return String((err as Record<string, unknown>).code);
    }
  }
  return undefined;
}

function extractErrorMessage(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const detail = (data as Record<string, unknown>).detail;
  if (typeof detail === "object" && detail !== null && "error" in detail) {
    const err = (detail as Record<string, unknown>).error;
    if (typeof err === "object" && err !== null && "message" in err) {
      return String((err as Record<string, unknown>).message);
    }
  }
  if (typeof detail === "string") return detail;
  return undefined;
}

function extractErrorDetails(data: unknown): unknown {
  if (typeof data !== "object" || data === null) return undefined;
  const detail = (data as Record<string, unknown>).detail;
  if (typeof detail === "object" && detail !== null && "error" in detail) {
    const err = (detail as Record<string, unknown>).error;
    if (typeof err === "object" && err !== null && "details" in err) {
      return (err as Record<string, unknown>).details;
    }
  }
  return undefined;
}
